import { NgFor, NgSwitch, NgSwitchCase } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostBinding, Input } from '@angular/core';
import { IconName, IconNode, LUCIDE_ICONS } from '@webapp/core/constants/lucide-icons';

/**
 * A Lucide icon, stroked in the surrounding text colour. Purely decorative:
 * it is hidden from assistive technology, so the text next to it has to carry
 * the meaning.
 */
@Component({
  selector: 'app-icon',
  standalone: true,
  imports: [NgFor, NgSwitch, NgSwitchCase],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" [attr.stroke-width]="strokeWidth"
         stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">
      <ng-container *ngFor="let node of nodes" [ngSwitch]="node[0]">
        <svg:path *ngSwitchCase="'path'" [attr.d]="node[1]['d']" />
        <svg:circle *ngSwitchCase="'circle'" [attr.cx]="node[1]['cx']" [attr.cy]="node[1]['cy']"
                    [attr.r]="node[1]['r']" />
        <svg:rect *ngSwitchCase="'rect'" [attr.x]="node[1]['x']" [attr.y]="node[1]['y']"
                  [attr.width]="node[1]['width']" [attr.height]="node[1]['height']"
                  [attr.rx]="node[1]['rx']" [attr.ry]="node[1]['ry']" />
        <svg:line *ngSwitchCase="'line'" [attr.x1]="node[1]['x1']" [attr.y1]="node[1]['y1']"
                  [attr.x2]="node[1]['x2']" [attr.y2]="node[1]['y2']" />
        <svg:ellipse *ngSwitchCase="'ellipse'" [attr.cx]="node[1]['cx']" [attr.cy]="node[1]['cy']"
                     [attr.rx]="node[1]['rx']" [attr.ry]="node[1]['ry']" />
        <svg:polyline *ngSwitchCase="'polyline'" [attr.points]="node[1]['points']" />
        <svg:polygon *ngSwitchCase="'polygon'" [attr.points]="node[1]['points']" />
      </ng-container>
    </svg>
  `,
  styles: [`
    :host { display: inline-flex; flex: none; line-height: 0; }
    svg { display: block; width: 100%; height: 100%; }
  `],
})
export class IconComponent {
  @Input({ required: true }) name!: IconName;

  /** Edge length in px. */
  @Input() size = 18;

  @Input() strokeWidth = 2;

  @HostBinding('style.width.px')
  get width() {
    return this.size;
  }

  @HostBinding('style.height.px')
  get height() {
    return this.size;
  }

  get nodes(): readonly IconNode[] {
    return LUCIDE_ICONS[this.name] ?? [];
  }
}
